import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Award,
  Plus,
  Search,
  Download,
  Trash2,
  Edit,
  FileText,
  Building2,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Upload,
  FileDown,
  ClipboardCheck,
  User,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificatesPage = () => {
  const [certificates, setCertificates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [approvedAudits, setApprovedAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("approved-audits");
  
  // Approved audits filter states
  const [approvedSearchTerm, setApprovedSearchTerm] = useState("");
  const [approvedStageFilter, setApprovedStageFilter] = useState("all");
  const [approvedFacilityFilter, setApprovedFacilityFilter] = useState("all");
  const [approvedCertFilter, setApprovedCertFilter] = useState("all");
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_id: "",
    certificate_type: "stage2",
    certificate_number: "",
    gstc_certification_code: "",
    is_facility_closed: false,
    issue_date: new Date().toISOString().split("T")[0],
    first_verification_date: "",
    expiry_date: "",
    facility_logo_base64: "",
    qr_code_base64: "",
  });

  // Fetch data
  const fetchCertificates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/certificates`, { withCredentials: true });
      setCertificates(response.data || []);
    } catch (error) {
      console.error("Error fetching certificates:", error);
      toast.error("Sertifikalar yüklenemedi");
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  }, []);

  const fetchAudits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      setAudits(response.data || []);
    } catch (error) {
      console.error("Error fetching audits:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/certificates/stats/summary`, { withCredentials: true });
      setStats(response.data || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  // Fetch approved RoyalCert audits for certificate creation
  const fetchApprovedAudits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/royalcert-audits`, { 
        params: { status: "approved" },
        withCredentials: true 
      });
      // Filter to only approved audits
      const approved = (response.data || []).filter(a => a.status === "approved");
      setApprovedAudits(approved);
    } catch (error) {
      console.error("Error fetching approved audits:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCertificates(), fetchCustomers(), fetchAudits(), fetchStats(), fetchApprovedAudits()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCertificates, fetchCustomers, fetchAudits, fetchStats, fetchApprovedAudits]);

  // Calculate expiry date based on facility status
  const calculateExpiryDate = (issueDate, isClosed) => {
    if (!issueDate) return "";
    const date = new Date(issueDate);
    if (isClosed) {
      date.setMonth(date.getMonth() + 3); // 3 months for closed facilities
    } else {
      date.setFullYear(date.getFullYear() + 1); // 1 year for open facilities
    }
    return date.toISOString().split("T")[0];
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // When customer is selected, check their latest audit for facility status
      if (field === "customer_id" && value) {
        // Find the latest completed audit for this customer
        const customerAudits = audits
          .filter(a => a.customer_id === value && a.status === "completed")
          .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));
        
        if (customerAudits.length > 0) {
          const latestAudit = customerAudits[0];
          // Auto-fill facility status from audit
          if (latestAudit.is_facility_closed !== undefined) {
            updated.is_facility_closed = latestAudit.is_facility_closed;
            // Recalculate expiry date
            updated.expiry_date = calculateExpiryDate(prev.issue_date, latestAudit.is_facility_closed);
          }
        }
      }
      
      // Auto-calculate expiry date when issue_date or is_facility_closed changes
      if (field === "issue_date" || field === "is_facility_closed") {
        const issueDate = field === "issue_date" ? value : prev.issue_date;
        const isClosed = field === "is_facility_closed" ? value : prev.is_facility_closed;
        updated.expiry_date = calculateExpiryDate(issueDate, isClosed);
      }
      
      return updated;
    });
  };

  // Handle image upload
  const handleImageUpload = (field, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Dosya boyutu 2MB'dan küçük olmalıdır");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      handleFormChange(field, reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Create certificate
  const handleCreate = async () => {
    if (!formData.customer_id) {
      toast.error("Lütfen bir müşteri seçin");
      return;
    }
    if (!formData.certificate_number) {
      toast.error("Lütfen sertifika numarası girin");
      return;
    }
    if (!formData.first_verification_date) {
      toast.error("Lütfen ilk belgelendirme tarihini girin");
      return;
    }

    try {
      await axios.post(`${API}/certificates`, formData, { withCredentials: true });
      toast.success("Sertifika oluşturuldu");
      setShowCreateDialog(false);
      resetForm();
      fetchCertificates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sertifika oluşturulamadı");
    }
  };

  // Update certificate
  const handleUpdate = async () => {
    if (!selectedCertificate) return;

    try {
      await axios.put(
        `${API}/certificates/${selectedCertificate.certificate_id}`,
        formData,
        { withCredentials: true }
      );
      toast.success("Sertifika güncellendi");
      setShowEditDialog(false);
      setSelectedCertificate(null);
      resetForm();
      fetchCertificates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sertifika güncellenemedi");
    }
  };

  // Delete certificate
  const handleDelete = async () => {
    if (!selectedCertificate) return;

    try {
      await axios.delete(
        `${API}/certificates/${selectedCertificate.certificate_id}`,
        { withCredentials: true }
      );
      toast.success("Sertifika silindi");
      setShowDeleteDialog(false);
      setSelectedCertificate(null);
      fetchCertificates();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sertifika silinemedi");
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (certificate) => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/certificates/${certificate.certificate_id}/generate`,
        {},
        { withCredentials: true, responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Sertifika_${certificate.certificate_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF oluşturuldu ve indirildi");
      fetchCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF oluşturulamadı");
    } finally {
      setGenerating(false);
    }
  };

  // Download DOCX (Word format)
  const handleDownloadDOCX = async (certificate) => {
    try {
      const response = await axios.get(
        `${API}/certificates/${certificate.certificate_id}/download-docx`,
        { withCredentials: true, responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Sertifika_${certificate.certificate_number}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Word dosyası indirildi");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Word dosyası indirilemedi");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      customer_id: "",
      certificate_type: "stage2",
      certificate_number: "",
      gstc_certification_code: "",
      is_facility_closed: false,
      issue_date: new Date().toISOString().split("T")[0],
      first_verification_date: "",
      expiry_date: "",
      facility_logo_base64: "",
      qr_code_base64: "",
    });
  };

  // Open edit dialog
  const openEditDialog = (certificate) => {
    setSelectedCertificate(certificate);
    setFormData({
      customer_id: certificate.customer_id,
      certificate_type: certificate.certificate_type,
      certificate_number: certificate.certificate_number,
      gstc_certification_code: certificate.gstc_certification_code || "",
      is_facility_closed: certificate.is_facility_closed,
      issue_date: certificate.issue_date,
      first_verification_date: certificate.first_verification_date,
      expiry_date: certificate.expiry_date,
      facility_logo_base64: certificate.facility_logo_base64 || "",
      qr_code_base64: certificate.qr_code_base64 || "",
    });
    setShowEditDialog(true);
  };

  // Filter certificates
  const filteredCertificates = certificates.filter(cert => {
    const matchesSearch = 
      cert.certificate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === "all" || cert.certificate_type === filterType;
    const matchesStatus = filterStatus === "all" || cert.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR");
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: "Taslak", variant: "secondary", icon: Clock },
      issued: { label: "Düzenlendi", variant: "default", icon: CheckCircle2 },
      expired: { label: "Süresi Dolmuş", variant: "destructive", icon: XCircle },
      revoked: { label: "İptal Edildi", variant: "destructive", icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Get type badge
  const getTypeBadge = (type, isClosed) => {
    const isStage2 = type?.includes("stage2");
    const isClosedType = type?.includes("_closed") || isClosed;
    const closedSuffix = isClosedType ? " (Kapalı)" : "";
    
    return (
      <Badge variant={isStage2 ? "outline" : "default"} className={isStage2 ? "border-orange-500 text-orange-600" : "bg-green-600"}>
        {isStage2 ? `2. Aşama${closedSuffix}` : `3. Aşama${closedSuffix}`}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="certificates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-7 h-7 text-[#1A4D2E]" />
            Sertifika Yönetimi
          </h1>
          <p className="text-gray-500 mt-1">
            Çevresel ve Kültürel Sürdürülebilirlik Sertifikaları
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="bg-[#1A4D2E] hover:bg-[#143D24]"
          data-testid="new-certificate-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Sertifika
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-gray-900">{stats.total || 0}</div>
            <p className="text-xs text-gray-500">Toplam</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-green-600">{stats.issued || 0}</div>
            <p className="text-xs text-gray-500">Düzenlendi</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-gray-500">{stats.draft || 0}</div>
            <p className="text-xs text-gray-500">Taslak</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-red-600">{stats.expired || 0}</div>
            <p className="text-xs text-gray-500">Süresi Dolmuş</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-orange-500">{stats.stage2 || 0}</div>
            <p className="text-xs text-gray-500">2. Aşama</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-[#1A4D2E]">{stats.stage3 || 0}</div>
            <p className="text-xs text-gray-500">3. Aşama</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="text-2xl font-bold text-amber-500">{stats.expiring_soon || 0}</div>
            <p className="text-xs text-gray-500">30 Gün İçinde</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Certificates and Approved Audits */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="approved-audits" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Onaylı Denetimler ({approvedAudits.length})
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            Sertifikalar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="certificates" className="mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Sertifika no veya müşteri ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="certificate-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tüm Tipler" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Tipler</SelectItem>
            <SelectItem value="stage2">2. Aşama</SelectItem>
            <SelectItem value="stage3">3. Aşama</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tüm Durumlar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="draft">Taslak</SelectItem>
            <SelectItem value="issued">Düzenlendi</SelectItem>
            <SelectItem value="expired">Süresi Dolmuş</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Certificates Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sertifika No</TableHead>
                <TableHead>Müşteri</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Düzenleme Tarihi</TableHead>
                <TableHead>Geçerlilik</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCertificates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    {searchTerm || filterType !== "all" || filterStatus !== "all"
                      ? "Arama kriterlerine uygun sertifika bulunamadı"
                      : "Henüz sertifika oluşturulmamış"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCertificates.map((cert) => (
                  <TableRow key={cert.certificate_id} data-testid={`cert-row-${cert.certificate_id}`}>
                    <TableCell className="font-medium">{cert.certificate_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="font-medium">{cert.customer_name}</div>
                          {cert.customer_brand && (
                            <div className="text-xs text-gray-500">{cert.customer_brand}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(cert.certificate_type, cert.is_facility_closed)}</TableCell>
                    <TableCell>{formatDate(cert.issue_date)}</TableCell>
                    <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                    <TableCell>{getStatusBadge(cert.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleGeneratePDF(cert)}
                          disabled={generating}
                          title="PDF İndir"
                          data-testid={`download-btn-${cert.certificate_id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDownloadDOCX(cert)}
                          title="Word İndir"
                          data-testid={`download-docx-btn-${cert.certificate_id}`}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <FileDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditDialog(cert)}
                          title="Düzenle"
                          data-testid={`edit-btn-${cert.certificate_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setSelectedCertificate(cert);
                            setShowDeleteDialog(true);
                          }}
                          title="Sil"
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-btn-${cert.certificate_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>

        {/* Approved Audits Tab */}
        <TabsContent value="approved-audits" className="mt-4">
          {/* Filters for Approved Audits */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Müşteri ara..."
                value={approvedSearchTerm}
                onChange={(e) => setApprovedSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="approved-search-input"
              />
            </div>
            <Select value={approvedStageFilter} onValueChange={setApprovedStageFilter}>
              <SelectTrigger className="w-[160px]" data-testid="approved-stage-filter">
                <SelectValue placeholder="Aşama" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Aşamalar</SelectItem>
                <SelectItem value="stage2">2. Aşama</SelectItem>
                <SelectItem value="stage3">3. Aşama</SelectItem>
              </SelectContent>
            </Select>
            <Select value={approvedFacilityFilter} onValueChange={setApprovedFacilityFilter}>
              <SelectTrigger className="w-[160px]" data-testid="approved-facility-filter">
                <SelectValue placeholder="Tesis Durumu" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Tesisler</SelectItem>
                <SelectItem value="open">Açık Tesis</SelectItem>
                <SelectItem value="closed">Kapalı Tesis</SelectItem>
              </SelectContent>
            </Select>
            <Select value={approvedCertFilter} onValueChange={setApprovedCertFilter}>
              <SelectTrigger className="w-[180px]" data-testid="approved-cert-filter">
                <SelectValue placeholder="Sertifika Durumu" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="pending">Sertifika Bekliyor</SelectItem>
                <SelectItem value="issued">Sertifika Verilmiş</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5 text-[#1A4D2E]" />
                Sertifika Bekleyen Onaylı Denetimler
                <Badge variant="secondary" className="ml-2">
                  {approvedAudits.filter(audit => {
                    const hasCertificate = certificates.some(c => c.customer_id === audit.customer_id);
                    const isClosed = audit.is_facility_closed || false;
                    const auditType = audit.audit_type || audit.auto_fill_data?.audit?.audit_type || "";
                    const auditPhase = String(audit.audit_phase || audit.customer?.audit_phase || "").toLowerCase();
                    
                    const isStage3 = auditType.toLowerCase().includes("stage3") || 
                                     auditType.toLowerCase().includes("3. aşama") ||
                                     auditPhase === "3" || auditPhase.includes("3. aşama");
                    const isStage2 = !isStage3 && (audit.is_stage2 === true || 
                                     auditType.toLowerCase().includes("stage2") || 
                                     auditType.toLowerCase().includes("2. aşama") ||
                                     auditPhase === "2" || auditPhase.includes("2. aşama"));
                    
                    const matchesSearch = !approvedSearchTerm || 
                      audit.customer_name?.toLowerCase().includes(approvedSearchTerm.toLowerCase());
                    const matchesStage = approvedStageFilter === "all" || 
                      (approvedStageFilter === "stage2" && isStage2) ||
                      (approvedStageFilter === "stage3" && !isStage2);
                    const matchesFacility = approvedFacilityFilter === "all" ||
                      (approvedFacilityFilter === "closed" && isClosed) ||
                      (approvedFacilityFilter === "open" && !isClosed);
                    const matchesCert = approvedCertFilter === "all" ||
                      (approvedCertFilter === "pending" && !hasCertificate) ||
                      (approvedCertFilter === "issued" && hasCertificate);
                    
                    return matchesSearch && matchesStage && matchesFacility && matchesCert;
                  }).length} kayıt
                </Badge>
              </CardTitle>
              <CardDescription>
                Raporu onaylanmış ve sertifika düzenlenmeyi bekleyen denetimler
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Müşteri</TableHead>
                    <TableHead>Denetim Tipi</TableHead>
                    <TableHead>Tesis Durumu</TableHead>
                    <TableHead>Denetim Tarihi</TableHead>
                    <TableHead>Denetçi</TableHead>
                    <TableHead>Onay Tarihi</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const filteredApprovedAudits = approvedAudits.filter(audit => {
                      const hasCertificate = certificates.some(c => c.customer_id === audit.customer_id);
                      const isClosed = audit.is_facility_closed || false;
                      const auditType = audit.audit_type || audit.auto_fill_data?.audit?.audit_type || "";
                      const auditPhase = String(audit.audit_phase || audit.customer?.audit_phase || "").toLowerCase();
                      
                      const isStage3 = auditType.toLowerCase().includes("stage3") || 
                                       auditType.toLowerCase().includes("3. aşama") ||
                                       auditPhase === "3" || auditPhase.includes("3. aşama");
                      const isStage2 = !isStage3 && (audit.is_stage2 === true || 
                                       auditType.toLowerCase().includes("stage2") || 
                                       auditType.toLowerCase().includes("2. aşama") ||
                                       auditPhase === "2" || auditPhase.includes("2. aşama"));
                      
                      const matchesSearch = !approvedSearchTerm || 
                        audit.customer_name?.toLowerCase().includes(approvedSearchTerm.toLowerCase());
                      const matchesStage = approvedStageFilter === "all" || 
                        (approvedStageFilter === "stage2" && isStage2) ||
                        (approvedStageFilter === "stage3" && !isStage2);
                      const matchesFacility = approvedFacilityFilter === "all" ||
                        (approvedFacilityFilter === "closed" && isClosed) ||
                        (approvedFacilityFilter === "open" && !isClosed);
                      const matchesCert = approvedCertFilter === "all" ||
                        (approvedCertFilter === "pending" && !hasCertificate) ||
                        (approvedCertFilter === "issued" && hasCertificate);
                      
                      return matchesSearch && matchesStage && matchesFacility && matchesCert;
                    });
                    
                    if (filteredApprovedAudits.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                            {approvedAudits.length === 0 
                              ? "Sertifika bekleyen onaylı denetim bulunmuyor"
                              : "Filtrelere uygun denetim bulunamadı"}
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return filteredApprovedAudits.map((audit) => {
                      // Check if certificate already exists for this customer
                      const hasCertificate = certificates.some(c => c.customer_id === audit.customer_id);
                      const isClosed = audit.is_facility_closed || false;
                      const auditType = audit.audit_type || audit.auto_fill_data?.audit?.audit_type || audit.auto_fill_data?.audit?.certification_type || "";
                      
                      // Check audit_phase from customer or audit data
                      const auditPhase = audit.audit_phase || audit.customer?.audit_phase || audit.auto_fill_data?.customer?.audit_phase || "";
                      const auditPhaseStr = String(auditPhase).toLowerCase();
                      
                      // Determine if Stage 2 or Stage 3
                      const isStage2Flag = audit.is_stage2 === true;
                      const isStage2Type = auditType.toLowerCase().includes("stage2") || 
                                           auditType.toLowerCase().includes("2. aşama") || 
                                           auditType.includes("_2");
                      const isStage2Phase = auditPhaseStr === "2" || 
                                            auditPhaseStr === "stage2" || 
                                            auditPhaseStr.includes("2. aşama");
                      
                      // Stage 3 explicit checks
                      const isStage3Type = auditType.toLowerCase().includes("stage3") || 
                                           auditType.toLowerCase().includes("3. aşama") || 
                                           auditType.includes("_3");
                      const isStage3Phase = auditPhaseStr === "3" || 
                                            auditPhaseStr === "stage3" || 
                                            auditPhaseStr.includes("3. aşama");
                      
                      // Prioritize Stage 3 checks, then Stage 2
                      const isStage2 = !isStage3Type && !isStage3Phase && (isStage2Flag || isStage2Type || isStage2Phase);
                      
                      // Debug log
                      console.log("Audit stage detection:", {
                        customer: audit.customer_name,
                        auditType,
                        auditPhase,
                        isStage2Flag,
                        isStage3Type,
                        isStage3Phase,
                        finalIsStage2: isStage2,
                        isClosed
                      });
                      
                      return (
                        <TableRow key={audit.audit_record_id}>
                          <TableCell>
                            <div className="font-medium">{audit.customer_name}</div>
                            {audit.brand_name && (
                              <div className="text-xs text-gray-500">{audit.brand_name}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={isStage2 ? "outline" : "default"} className={isStage2 ? "border-orange-500 text-orange-600" : "bg-green-600"}>
                              {isStage2 ? "2. Aşama" : "3. Aşama"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={isClosed ? "secondary" : "default"} className={isClosed ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}>
                              <Building2 className="w-3 h-3 mr-1" />
                              {isClosed ? "Kapalı Tesis" : "Açık Tesis"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDate(audit.report_data?.header_data?.audit_date || audit.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 text-gray-400" />
                              {audit.report_data?.header_data?.team_leader || audit.created_by_name || "-"}
                            </div>
                          </TableCell>
                          <TableCell>
                            {formatDate(audit.reviewed_at || audit.updated_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            {hasCertificate ? (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Sertifika Var
                              </Badge>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => {
                                  // Pre-fill form with audit data
                                  const certType = isStage2 ? "stage2" : "stage3";
                                  console.log("Creating certificate:", { certType, isStage2, isClosed, auditPhase: audit.audit_phase });
                                  setFormData({
                                    customer_id: audit.customer_id,
                                    certificate_type: certType,
                                    certificate_number: "",
                                    gstc_certification_code: "",
                                    is_facility_closed: isClosed,
                                    issue_date: new Date().toISOString().split("T")[0],
                                    first_verification_date: "",
                                    expiry_date: calculateExpiryDate(new Date().toISOString().split("T")[0], isClosed),
                                    facility_logo_base64: "",
                                    qr_code_base64: "",
                                  });
                                  setShowCreateDialog(true);
                                }}
                                className="bg-[#1A4D2E] hover:bg-[#143D24]"
                                data-testid={`create-cert-btn-${audit.audit_record_id}`}
                              >
                                <Award className="w-4 h-4 mr-1" />
                                Sertifika Oluştur
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Sertifika Oluştur</DialogTitle>
            <DialogDescription>
              Çevresel ve Kültürel Sürdürülebilirlik Sertifikası bilgilerini girin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label htmlFor="customer">Müşteri *</Label>
              <Select
                value={formData.customer_id}
                onValueChange={(v) => handleFormChange("customer_id", v)}
              >
                <SelectTrigger data-testid="customer-select">
                  <SelectValue placeholder="Müşteri seçin" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.customer_id} value={customer.customer_id}>
                      {customer.name} {customer.brand_name ? `(${customer.brand_name})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Certificate Type */}
            <div className="space-y-2">
              <Label htmlFor="type">Sertifika Tipi *</Label>
              <Select
                value={formData.certificate_type}
                onValueChange={(v) => handleFormChange("certificate_type", v)}
              >
                <SelectTrigger data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage2">2. Aşama Belgesi</SelectItem>
                  <SelectItem value="stage3">3. Aşama Sertifikası</SelectItem>
                  <SelectItem value="stage2_closed">2. Aşama Belgesi (Kapalı Tesis)</SelectItem>
                  <SelectItem value="stage3_closed">3. Aşama Sertifikası (Kapalı Tesis)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Facility Closed - Auto sync with certificate type */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>İşletmeye Kapalı</Label>
                <p className="text-xs text-gray-500">
                  Kapalı işletmeler için geçerlilik 3 ay olarak hesaplanır
                </p>
              </div>
              <Switch
                checked={formData.is_facility_closed || formData.certificate_type?.includes('_closed')}
                onCheckedChange={(v) => {
                  // Update both is_facility_closed and certificate_type
                  const baseType = formData.certificate_type?.replace('_closed', '') || 'stage3';
                  const newType = v ? `${baseType}_closed` : baseType;
                  handleFormChange("is_facility_closed", v);
                  handleFormChange("certificate_type", newType);
                }}
                data-testid="facility-closed-switch"
              />
            </div>

            {/* Certificate Number */}
            <div className="space-y-2">
              <Label htmlFor="certNo">Sertifika Numarası *</Label>
              <Input
                id="certNo"
                value={formData.certificate_number}
                onChange={(e) => handleFormChange("certificate_number", e.target.value)}
                placeholder="RC-2025-001"
                data-testid="certificate-number-input"
              />
            </div>

            {/* GSTC Certification Code - Only for Stage 3 (including closed) */}
            {(formData.certificate_type === "stage3" || formData.certificate_type === "stage3_closed") && (
              <div className="space-y-2">
                <Label htmlFor="gstcCode">GSTC Sertifikasyon Kodu</Label>
                <Input
                  id="gstcCode"
                  value={formData.gstc_certification_code}
                  onChange={(e) => handleFormChange("gstc_certification_code", e.target.value)}
                  placeholder="GSTC-TR-XXXXX"
                  data-testid="gstc-code-input"
                />
                <p className="text-xs text-gray-500">
                  3. Aşama sertifikaları için GSTC sertifikasyon kodu
                </p>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstDate">İlk Belgelendirme *</Label>
                <Input
                  id="firstDate"
                  type="date"
                  value={formData.first_verification_date}
                  onChange={(e) => handleFormChange("first_verification_date", e.target.value)}
                  data-testid="first-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="issueDate">Düzenleme Tarihi *</Label>
                <Input
                  id="issueDate"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => handleFormChange("issue_date", e.target.value)}
                  data-testid="issue-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Geçerlilik Tarihi</Label>
                <Input
                  id="expiryDate"
                  type="date"
                  value={formData.expiry_date}
                  readOnly
                  className="bg-gray-50"
                  data-testid="expiry-date-input"
                />
                <p className="text-xs text-gray-500">
                  {formData.is_facility_closed ? "3 ay (Kapalı)" : "1 yıl"}
                </p>
              </div>
            </div>

            {/* Image Uploads */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>İşletme Logosu</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {formData.facility_logo_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={formData.facility_logo_base64}
                        alt="Logo"
                        className="max-h-20 mx-auto"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFormChange("facility_logo_base64", "")}
                      >
                        Kaldır
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Logo yükle</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload("facility_logo_base64", e)}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>QR Kod</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {formData.qr_code_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={formData.qr_code_base64}
                        alt="QR"
                        className="max-h-20 mx-auto"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFormChange("qr_code_base64", "")}
                      >
                        Kaldır
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">QR kod yükle</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload("qr_code_base64", e)}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleCreate} className="bg-[#1A4D2E] hover:bg-[#143D24]">
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sertifika Düzenle</DialogTitle>
            <DialogDescription>
              Sertifika bilgilerini güncelleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Same form fields as create */}
            <div className="space-y-2">
              <Label htmlFor="type">Sertifika Tipi</Label>
              <Select
                value={formData.certificate_type}
                onValueChange={(v) => handleFormChange("certificate_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage2">2. Aşama Belgesi</SelectItem>
                  <SelectItem value="stage3">3. Aşama Sertifikası</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>İşletmeye Kapalı</Label>
                <p className="text-xs text-gray-500">
                  Kapalı işletmeler için geçerlilik 3 ay olarak hesaplanır
                </p>
              </div>
              <Switch
                checked={formData.is_facility_closed}
                onCheckedChange={(v) => handleFormChange("is_facility_closed", v)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="certNo">Sertifika Numarası</Label>
              <Input
                id="certNo"
                value={formData.certificate_number}
                onChange={(e) => handleFormChange("certificate_number", e.target.value)}
              />
            </div>

            {/* GSTC Certification Code - Only for Stage 3 (including closed) */}
            {(formData.certificate_type === "stage3" || formData.certificate_type === "stage3_closed") && (
              <div className="space-y-2">
                <Label htmlFor="gstcCodeEdit">GSTC Sertifikasyon Kodu</Label>
                <Input
                  id="gstcCodeEdit"
                  value={formData.gstc_certification_code}
                  onChange={(e) => handleFormChange("gstc_certification_code", e.target.value)}
                  placeholder="GSTC-TR-XXXXX"
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>İlk Belgelendirme</Label>
                <Input
                  type="date"
                  value={formData.first_verification_date}
                  onChange={(e) => handleFormChange("first_verification_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Düzenleme Tarihi</Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => handleFormChange("issue_date", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Geçerlilik Tarihi</Label>
                <Input
                  type="date"
                  value={formData.expiry_date}
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>İşletme Logosu</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {formData.facility_logo_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={formData.facility_logo_base64}
                        alt="Logo"
                        className="max-h-20 mx-auto"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFormChange("facility_logo_base64", "")}
                      >
                        Kaldır
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">Logo yükle</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload("facility_logo_base64", e)}
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>QR Kod</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {formData.qr_code_base64 ? (
                    <div className="space-y-2">
                      <img
                        src={formData.qr_code_base64}
                        alt="QR"
                        className="max-h-20 mx-auto"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFormChange("qr_code_base64", "")}
                      >
                        Kaldır
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">QR kod yükle</p>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload("qr_code_base64", e)}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpdate} className="bg-[#1A4D2E] hover:bg-[#143D24]">
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sertifikayı Sil</DialogTitle>
            <DialogDescription>
              Bu sertifikayı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              <strong>Sertifika No:</strong> {selectedCertificate?.certificate_number}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Müşteri:</strong> {selectedCertificate?.customer_name}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificatesPage;
